/**
 * The waypoint marker: a GoldFinder pin.
 *
 * Waypoints used to draw as TerraDraw's default 12px blue disc, which looked
 * like a data point rather than something you placed, and sat on the map
 * indistinguishable from a soil sample. A pin reads as "I put this here", and
 * anchors to an exact spot the way a disc centred on the coordinate does not.
 *
 * The geometry is the logo mark from `landing/assets/gf-mark.svg` - the same
 * pin outline and the same pickaxe - so a waypoint is visibly the app's own
 * thing. One deliberate difference: the pick is filled white here rather than
 * knocked out to transparent. Over satellite imagery a transparent pick shows
 * whatever is underneath and turns to mud; white holds at 40px on any base map.
 */

/**
 * Marker colours. Prospectors use colour to mean something - checked / worth a
 * return / dug already - so the set is small, and every one holds against both
 * red dirt and green scrub. Keys are stored on the feature, so renaming one
 * orphans existing markers; add, don't rename.
 */
import { WAYPOINT_BODY_SVG, WAYPOINT_GLYPH_SVG } from './waypointGlyphs.generated';

export const WAYPOINT_COLOURS = [
  { key: 'orange', label: 'Orange', hex: '#FD7218' },
  { key: 'red',    label: 'Red',    hex: '#DC2626' },
  { key: 'yellow', label: 'Gold',   hex: '#F5B301' },
  { key: 'green',  label: 'Green',  hex: '#12924F' },
  { key: 'blue',   label: 'Blue',   hex: '#2563EB' },
  { key: 'purple', label: 'Purple', hex: '#7B2D8E' },
  { key: 'black',  label: 'Black',  hex: '#22262B' },
];

export const DEFAULT_WAYPOINT_COLOUR = 'orange';

const ORANGE = '#FD7218';

/**
 * viewBox is 32x42 with the pin tip at (16, 41), so `icon-anchor: bottom`
 * puts the point of the pin on the coordinate rather than its middle.
 *
 * The BODY and the GLYPH are separate images on separate layers. 7 icons x
 * 7 colours is 49 combinations, and baking them together would mean generating
 * all 49 (and re-generating on every new colour) to render at most a handful.
 * Split, a colour costs one image and an icon costs one image.
 *
 * They register because the glyph SVGs use the SAME 32x42 canvas with the glyph
 * already placed in the pin head, and both layers anchor bottom - so alignment
 * is a property of the artwork, not an offset to keep in sync with mobile.
 */
const pinSvg = ({ fill = ORANGE } = {}) => WAYPOINT_BODY_SVG.replace('__FILL__', fill);

/** One image per colour, plus a plain default for markers saved before colours. */
const pinImageId = (key) => `gf-waypoint-${key}`;
const glyphImageId = (key) => `gf-glyph-${key}`;

/**
 * Every waypoint saved before icons reached the map carries no `icon` at all,
 * so the fallback has to be the mark those waypoints already showed: the
 * pickaxe. That is why `pin` draws the pick rather than a nested map-pin - the
 * body is already the pin, and a pin inside a pin reads as a mistake.
 */
export const DEFAULT_WAYPOINT_ICON = 'pin';

const PINS = Object.fromEntries([
  ['gf-waypoint', pinSvg({ fill: ORANGE })],
  ...WAYPOINT_COLOURS.map((c) => [pinImageId(c.key), pinSvg({ fill: c.hex })]),
  ...Object.entries(WAYPOINT_GLYPH_SVG).map(([k, svg]) => [glyphImageId(k), svg]),
]);

/**
 * Rasterises one SVG and registers it as a map image.
 *
 * Drawn at 2x and registered with pixelRatio 2 so the pin stays sharp on a
 * HiDPI screen. Width and height are set on the <svg> element itself because
 * Firefox will not size an SVG loaded through an <img> from viewBox alone.
 */
/**
 * hasImage throws once the style has been torn down (it reaches through an
 * undefined style to getImage). The image load below is async, so it can and
 * does resolve after the map has gone - every call has to tolerate that.
 */
const styleAlive = (map) => {
  try {
    return !!map && typeof map.hasImage === 'function' && !!map.getStyle();
  } catch {
    return false;
  }
};

function addPin(map, id, svg, pixelRatio = 2) {
  return new Promise((resolve) => {
    if (!styleAlive(map) || map.hasImage(id)) return resolve();
    const img = new Image();
    img.onload = () => {
      if (!styleAlive(map)) return resolve();
      const w = img.width * pixelRatio;
      const h = img.height * pixelRatio;
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);
      try {
        if (!map.hasImage(id)) {
          map.addImage(id, ctx.getImageData(0, 0, w, h), { pixelRatio });
        }
      } catch { /* style went away between the check and the add */ }
      resolve();
    };
    // A pin that fails to load must not block the layer being added - the
    // layer would simply render nothing, which is the current behaviour anyway.
    img.onerror = () => resolve();
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  });
}

/** Registers every waypoint pin. Safe to call more than once. */
export function addWaypointPins(map) {
  return Promise.all(
    Object.entries(PINS).map(([id, svg]) => addPin(map, id, svg))
  );
}

export const WAYPOINT_PIN_LAYER = 'gf-waypoint-pins';
export const WAYPOINT_GLYPH_LAYER = 'gf-waypoint-glyphs';
export const WAYPOINT_SOURCE = 'gf-waypoints';

/**
 * Layout for the pin symbol layer.
 *
 * This used to be added imperatively onto TerraDraw's own source. That could
 * not work: TerraDraw creates its source LAZILY, only once it has a feature to
 * draw. On a fresh load with no waypoints yet there was nothing to attach to,
 * the install poll expired after four seconds, and the layer was never added -
 * so clicking a pin hit nothing. Waypoints are our data anyway (they live in
 * Firestore), so they get their own source that always exists.
 */
export const waypointLayerLayout = {
  // Colour comes off the feature. A `match` rather than building the image
  // name with `concat`: an unknown or missing key falls to the default instead
  // of asking for an image that does not exist, which renders nothing at all.
  'icon-image': [
    'match',
    ['coalesce', ['get', 'colour'], DEFAULT_WAYPOINT_COLOUR],
    ...WAYPOINT_COLOURS.flatMap((c) => [c.key, pinImageId(c.key)]),
    pinImageId(DEFAULT_WAYPOINT_COLOUR),
  ],
  'icon-anchor': 'bottom',
  // Waypoints are placed deliberately, so never drop one for collision - a pin
  // the user put down that silently disappears is worse than two overlapping.
  'icon-allow-overlap': true,
  'icon-ignore-placement': true,
  'icon-size': 1,
};

/**
 * Layout for the glyph symbol layer, drawn directly above the body.
 *
 * Same anchor, same size, same overlap rules - overlap especially, because a
 * glyph collided away would leave a blank pin rather than simply not drawing.
 *
 * Waypoints created on the web carry no `icon` yet (there is no icon picker
 * here); ones synced from the mobile app do. The `coalesce` is what lets the two
 * share a renderer: a web waypoint falls to the pickaxe, a mobile one shows
 * whatever the user chose in the field.
 */
export const waypointGlyphLayerLayout = {
  'icon-image': [
    'match',
    ['coalesce', ['get', 'icon'], DEFAULT_WAYPOINT_ICON],
    ...Object.keys(WAYPOINT_GLYPH_SVG).flatMap((k) => [k, glyphImageId(k)]),
    glyphImageId(DEFAULT_WAYPOINT_ICON),
  ],
  'icon-anchor': 'bottom',
  'icon-allow-overlap': true,
  'icon-ignore-placement': true,
  'icon-size': 1,
};

/**
 * Lifts the pin layer to the top of the stack.
 *
 * react-map-gl inserts a <Layer> wherever its <Source> MOUNTS, and DrawControl
 * mounts early inside <Map> - so the pins landed underneath the base imagery.
 * They stayed hit-testable (queryRenderedFeatures ignores occlusion), which is
 * why clicks worked while nothing was visible: the pins were being painted
 * over by an opaque raster.
 *
 * Waypoints are the user's own marks and belong above everything, so rather
 * than pin them to one of the base/overlay anchors they are re-raised whenever
 * the style changes - which also covers overlays mounted later.
 */
export function raiseWaypointPinLayer(map) {
  if (!map || typeof map.getLayer !== 'function') return;
  try {
    if (!map.getLayer(WAYPOINT_PIN_LAYER)) return;
    const layers = map.getStyle()?.layers || [];
    const top = layers[layers.length - 1]?.id;
    // Order matters: the body moves first, then the glyph, so the glyph ends up
    // above it. Checking the GLYPH is on top is the right test - if it is, both
    // are already in place.
    if (top === WAYPOINT_GLYPH_LAYER) return;
    map.moveLayer(WAYPOINT_PIN_LAYER);
    if (map.getLayer(WAYPOINT_GLYPH_LAYER)) map.moveLayer(WAYPOINT_GLYPH_LAYER);
  } catch { /* style mid-update */ }
}
